import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolvePhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { DateFieldFr } from "@/components/DateFieldFr";
import { CascadeSelect } from "@/components/CascadeSelect";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Loader2, Upload, X, Camera, Check,
  ArrowLeft, ArrowRight, ShieldAlert, Package, ImageIcon, Hash, Coins, MapPin, CheckCircle2,
} from "lucide-react";
import { categoryIcon } from "@/lib/categoryIcons";

const STATES = ["vivant", "abattu", "pieces", "pret-a-cuire"];

const empty = {
  category: "poulet-de-chair", state: "vivant", quantity: 10, avg_weight: "1,8 kg",
  price: 4500, price_unit: "par tête", region: "", city: "",
  availability_date: new Date().toISOString().slice(0, 10),
  delivery_available: false, photos: [], comment: "", neighborhood: "",
};

const STEP_META = [
  { key: "product", Icon: Package },
  { key: "photo", Icon: ImageIcon },
  { key: "quantity", Icon: Hash },
  { key: "price", Icon: Coins },
  { key: "place", Icon: MapPin },
  { key: "publish", Icon: CheckCircle2 },
];

export default function PublishListing() {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(empty);
  const [step, setStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isEggs = form.category === "oeufs";

  const selectCategory = (slug) => {
    if (slug === "oeufs") setForm((f) => ({ ...f, category: "oeufs", state: "na", price_unit: "par plateau", avg_weight: "" }));
    else setForm((f) => ({ ...f, category: slug, state: f.state === "na" ? "vivant" : f.state, price_unit: f.price_unit === "par plateau" ? "par tête" : f.price_unit }));
  };

  const uploadPhotos = async (files) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, data.path] }));
      }
    } catch (e) { toast.error(t("listingForm.photoError") || "Échec de l'envoi de la photo"); }
    finally { setUploading(false); }
  };
  const removePhoto = (p) => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }));

  const submit = async () => {
    if (!form.region || !form.neighborhood) { toast.error(t("common.required")); setStep(4); return; }
    setSaving(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity), price: Number(form.price) };
      await api.post("/listings", payload);
      toast.success(t("listingForm.published"));
      navigate("/vendeur");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  if (!user?.verified) {
    return (
      <div className="container-page py-10 max-w-lg">
        <div className="rounded-2xl border border-accent/40 bg-accent/20 p-6 flex items-start gap-3" data-testid="publish-not-verified">
          <ShieldAlert className="h-6 w-6 text-primary mt-0.5 shrink-0" />
          <div>
            <p className="font-heading font-semibold text-foreground">{t("dashboard.notVerifiedTitle")}</p>
            <p className="text-sm text-muted-foreground mt-1">{t("verify.notice")}</p>
            <Button className="mt-4 rounded-full" onClick={() => navigate("/vendeur")} data-testid="publish-back-dashboard">{t("common.back")}</Button>
          </div>
        </div>
      </div>
    );
  }

  const canNext = () => {
    if (step === 0) return !!form.category;
    if (step === 2) return Number(form.quantity) > 0;
    if (step === 3) return Number(form.price) > 0;
    if (step === 4) return !!form.region && !!form.neighborhood;
    return true;
  };

  const next = () => { if (!canNext()) { toast.error(t("common.required")); return; } setStep((s) => Math.min(5, s + 1)); window.scrollTo(0, 0); };
  const back = () => { if (step === 0) navigate("/vendeur"); else { setStep((s) => s - 1); window.scrollTo(0, 0); } };

  const StepShell = ({ Icon, title, subtitle, children }) => (
    <div className="animate-fade-up">
      <div className="flex items-center gap-3 mb-5">
        <span className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-6 w-6" /></span>
        <div>
          <h1 className="font-heading font-extrabold text-2xl text-secondary leading-tight">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );

  return (
    <div className="container-page py-6 max-w-xl pb-28" data-testid="publish-wizard">
      {/* Progress */}
      <div className="flex items-center gap-1.5 mb-6">
        {STEP_META.map((s, i) => (
          <div key={s.key} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-muted"}`} data-testid={`publish-progress-${i}`} />
        ))}
      </div>
      <p className="text-xs font-semibold text-muted-foreground mb-4" data-testid="publish-step-label">
        {t("publish.stepOf", { n: step + 1, total: 6 })} · {t(`publish.steps.${STEP_META[step].key}`)}
      </p>

      {/* Step content */}
      {step === 0 && (
        <StepShell Icon={Package} title={t("publish.steps.product")} subtitle={t("listingForm.chooseCategory")}>
          <div className="grid grid-cols-3 gap-3">
            {categories.map((c) => {
              const active = form.category === c.slug;
              return (
                <button key={c.slug} type="button" onClick={() => selectCategory(c.slug)} data-testid={`publish-cat-${c.slug}`}
                  className={`min-h-[104px] rounded-2xl border-2 p-2 flex flex-col items-center justify-center gap-2 transition-colors active:scale-95 ${active ? "border-secondary bg-secondary/10 ring-2 ring-secondary/20" : "border-border bg-card hover:border-secondary/50"}`}>
                  <img src={categoryIcon(c.slug)} alt={t(`categories.${c.slug}`)} className="h-12 w-12 object-contain" loading="lazy" />
                  <span className="text-xs font-semibold text-secondary text-center leading-tight">{t(`categories.${c.slug}`)}</span>
                </button>
              );
            })}
          </div>
          {!isEggs && (
            <div className="mt-5">
              <Label>{t("listingForm.state")}</Label>
              <Select value={form.state} onValueChange={(v) => set("state", v)}>
                <SelectTrigger className="mt-1.5 h-12" data-testid="publish-state"><SelectValue /></SelectTrigger>
                <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{t(`states.${s}`)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </StepShell>
      )}

      {step === 1 && (
        <StepShell Icon={ImageIcon} title={t("publish.steps.photo")} subtitle={t("listingForm.photos")}>
          <div className="flex flex-wrap gap-3 items-center">
            {form.photos.map((p) => (
              <div key={p} className="relative h-24 w-24 rounded-xl overflow-hidden border border-border">
                <img src={resolvePhoto(p)} alt="" className="h-full w-full object-cover" />
                <button onClick={() => removePhoto(p)} type="button" className="absolute top-0.5 right-0.5 bg-red-600 rounded-full p-1 text-white" data-testid="publish-remove-photo"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <label className="h-24 w-28 rounded-xl border-2 border-dashed border-secondary/50 bg-secondary/5 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-secondary transition-colors" data-testid="publish-take-photo">
              {uploading ? <Loader2 className="h-7 w-7 animate-spin text-secondary" /> : <Camera className="h-8 w-8 text-secondary" />}
              <span className="text-[11px] font-semibold text-secondary">{t("listingForm.takePhoto")}</span>
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files && uploadPhotos(e.target.files)} data-testid="publish-camera-input" />
            </label>
            <label className="h-24 w-24 rounded-xl border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary transition-colors" data-testid="publish-gallery">
              <Upload className="h-6 w-6 text-muted-foreground" />
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files && uploadPhotos(e.target.files)} data-testid="publish-photo-input" />
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-3">{t("publish.photoHint")}</p>
        </StepShell>
      )}

      {step === 2 && (
        <StepShell Icon={Hash} title={t("publish.steps.quantity")}>
          <Label>{isEggs ? t("listingForm.plateaux") : t("listingForm.quantity")}</Label>
          <Input type="number" min={1} value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="mt-1.5 h-14 text-lg" data-testid="publish-quantity" />
          {!isEggs && (
            <div className="mt-5">
              <Label>{t("listingForm.avgWeight")}</Label>
              <Input value={form.avg_weight} onChange={(e) => set("avg_weight", e.target.value)} className="mt-1.5 h-14 text-lg" data-testid="publish-weight" />
            </div>
          )}
        </StepShell>
      )}

      {step === 3 && (
        <StepShell Icon={Coins} title={t("publish.steps.price")}>
          <Label>{t("listingForm.price")}</Label>
          <Input type="number" min={0} value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1.5 h-14 text-lg" data-testid="publish-price" />
          <div className="mt-5">
            <Label>{t("listingForm.priceUnit")}</Label>
            {isEggs ? (
              <Input value={t("listingForm.perPlateau")} disabled className="mt-1.5 h-12" data-testid="publish-price-unit" />
            ) : (
              <Select value={form.price_unit} onValueChange={(v) => set("price_unit", v)}>
                <SelectTrigger className="mt-1.5 h-12" data-testid="publish-price-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{["par tête", "par kg", "par lot"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div className="mt-5">
            <Label>{t("listingForm.availabilityDate")}</Label>
            <div className="mt-1.5"><DateFieldFr value={form.availability_date} onChange={(v) => set("availability_date", v)} testid="publish-date" className="h-12" /></div>
          </div>
          <div className="mt-5 flex items-center justify-between rounded-xl border border-border px-4 h-14">
            <Label className="cursor-pointer">{t("listingForm.delivery")}</Label>
            <Switch checked={form.delivery_available} onCheckedChange={(v) => set("delivery_available", v)} data-testid="publish-delivery" />
          </div>
        </StepShell>
      )}

      {step === 4 && (
        <StepShell Icon={MapPin} title={t("publish.steps.place")}>
          <CascadeSelect region={form.region} city={form.city} neighborhood={form.neighborhood} onRegion={(v) => set("region", v)} onCity={(v) => set("city", v)} onNeighborhood={(v) => set("neighborhood", v)} large testid="publish-cascade" />
          <div className="mt-5">
            <Label>{t("listingForm.comment")}</Label>
            <Textarea value={form.comment} onChange={(e) => set("comment", e.target.value)} placeholder={t("listingForm.commentPlaceholder")} className="mt-1.5" rows={3} data-testid="publish-comment" />
          </div>
        </StepShell>
      )}

      {step === 5 && (
        <StepShell Icon={CheckCircle2} title={t("publish.steps.publish")} subtitle={t("publish.reviewHint")}>
          <div className="rounded-2xl bg-card border border-border overflow-hidden" data-testid="publish-review">
            {form.photos[0] && <img src={resolvePhoto(form.photos[0])} alt="" className="h-40 w-full object-cover" />}
            <div className="p-4 space-y-1.5 text-sm">
              <p className="font-heading font-bold text-secondary text-lg">{t(`categories.${form.category}`)}{!isEggs && ` — ${t(`states.${form.state}`)}`}</p>
              <p className="text-muted-foreground">{isEggs ? t("listingForm.plateaux") : t("listingForm.quantity")}: <span className="text-foreground font-medium">{form.quantity}</span>{!isEggs && form.avg_weight ? ` · ${form.avg_weight}` : ""}</p>
              <p className="text-muted-foreground">{t("listingForm.price")}: <span className="text-primary font-bold">{form.price} FCFA</span> {isEggs ? t("listingForm.perPlateau") : form.price_unit}</p>
              <p className="text-muted-foreground">{t("publish.steps.place")}: <span className="text-foreground font-medium">{[form.neighborhood, form.city, form.region].filter(Boolean).join(", ") || "—"}</span></p>
              <p className="text-muted-foreground">{t("listingForm.delivery")}: <span className="text-foreground font-medium">{form.delivery_available ? t("common.yes") : t("common.no")}</span></p>
            </div>
          </div>
        </StepShell>
      )}

      {/* Fixed bottom action bar */}
      <div className="fixed bottom-16 md:bottom-0 inset-x-0 bg-card border-t border-border p-3 z-40">
        <div className="container-page max-w-xl flex gap-3">
          <Button variant="outline" onClick={back} className="h-13 flex-1 rounded-2xl h-14 text-base font-bold" data-testid="publish-back">
            <ArrowLeft className="h-5 w-5 mr-1.5" /> {step === 0 ? t("common.cancel") : t("common.back")}
          </Button>
          {step < 5 ? (
            <Button onClick={next} className="flex-1 rounded-2xl h-14 text-base font-bold bg-primary hover:bg-primary/90" data-testid="publish-next">
              {t("common.next")} <ArrowRight className="h-5 w-5 ml-1.5" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving} className="flex-1 rounded-2xl h-14 text-base font-bold bg-green-600 hover:bg-green-700 text-white" data-testid="publish-submit">
              {saving ? <Loader2 className="h-5 w-5 mr-1.5 animate-spin" /> : <Check className="h-5 w-5 mr-1.5" />} {t("publish.publishNow")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
